import React, { useCallback, useEffect, useState } from 'react';

import { EmptyState, ErrorState, ListaSelecao, Modal, Skeleton } from '../../../ui';

import { fetchSgaCotas, Cota } from '../../../api/sga-cota/sga-cotaApi';
import { parseMoeda } from '../../../utils/moeda';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Escolha da cota (reescrito na at-59 sobre o `Modal` do kit).
 *
 * ## O que a reescrita mudou
 *
 *   1. **Visual do design system** e lista alcançável pelo teclado (era
 *      `<li onClick>`).
 *   2. **"Faltam dados" deixou de parecer "não achei nada".** Os dois casos caíam
 *      no mesmo `<p>{errorMessage}</p>`: quando faltava regional, cooperativa,
 *      tipo ou valor, o consultor lia uma frase seca sem saber o que fazer. Agora
 *      a precondição vira `EmptyState` explicando **o que preencher**, e só falha
 *      de rede vira `ErrorState` com "Tentar novamente".
 *   3. **Falha de rede parava em `toast` + mensagem fixa** ("Nenhuma cota
 *      encontrada para os parâmetros inseridos") — que descreve o caso *vazio*, e
 *      não o de erro. São coisas diferentes e agora aparecem diferentes.
 *
 * ## O que NÃO mudou, de propósito
 *
 * ⚠️ **As props são as mesmas**: o modal é usado por `views/cotacao` e pelo
 * `FormularioVeiculo` (que atende as 3 telas de veículo). 📌 **Atualização da
 * at-62:** as duas são migradas — não há mais consumidor legado aqui.
 * 🔴 **`parseMoeda` continua sendo o parse do valor** (at-30): o antigo
 * `/\D/g` + `/100` assumia string mascarada de 2 casas e transformava "80668"
 * cru em 806,68 — faixa de cota errada, em silêncio.
 * 🔴 **A faixa é a do valor PROTEGIDO** (at-30). Quem decide isso é quem chama
 * (o `FormularioVeiculo` manda `valorProtegido || valorFipe`); aqui é só `valorFipe`
 * por nome de prop, e renomear obrigaria a tocar nas telas legadas.
 */

interface ModalCotasProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCota: (cota: Cota) => void;
  codigoRegional: string;
  codigoCooperativa: string;
  codigoTipoVeiculo: string;
  valorFipe: string;
}

type Estado = 'carregando' | 'ok' | 'erro' | 'faltam-dados';

const ModalCotas: React.FC<ModalCotasProps> = ({
  isOpen,
  onClose,
  onSelectCota,
  codigoRegional,
  codigoCooperativa,
  codigoTipoVeiculo,
  valorFipe,
}) => {
  const [cotas, setCotas] = useState<Cota[]>([]);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  const [faltando, setFaltando] = useState<string[]>([]);

  const buscar = useCallback(async () => {
    /*
     * Precondição, não erro: sem estes quatro a consulta nem faz sentido. Dizer
     * QUAIS faltam é o que o modal antigo não fazia — ele mostrava a mesma frase
     * genérica para isso e para falha de rede.
     */
    const ausentes: string[] = [];
    if (!codigoRegional) ausentes.push('Regional');
    if (!codigoCooperativa) ausentes.push('Cooperativa');
    if (!codigoTipoVeiculo) ausentes.push('Tipo de veículo');

    // at-30: parse por estrutura — ver o aviso no topo.
    const valor = parseMoeda(valorFipe);
    if (!valorFipe || isNaN(valor) || valor <= 0) ausentes.push('Valor protegido');

    if (ausentes.length > 0) {
      setFaltando(ausentes);
      setEstado('faltam-dados');
      return;
    }

    setEstado('carregando');
    try {
      const resposta = await fetchSgaCotas(
        codigoRegional,
        codigoCooperativa,
        codigoTipoVeiculo,
        valor.toString(),
      );

      setCotas(Array.isArray(resposta) ? resposta : []);
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar as cotas.'));
      setEstado('erro');
    }
  }, [codigoRegional, codigoCooperativa, codigoTipoVeiculo, valorFipe]);

  useEffect(() => {
    if (isOpen) void buscar();
  }, [isOpen, buscar]);

  return (
    <Modal
      aberto={isOpen}
      titulo="Selecionar Cota"
      descricao="A faixa é definida pelo valor protegido do veículo."
      tamanho="sm"
      aoFechar={onClose}
    >
      {estado === 'carregando' && <Skeleton linhas={4} altura="tw-h-11" />}

      {estado === 'faltam-dados' && (
        <EmptyState
          icone="alerta"
          titulo="Faltam dados para buscar a cota"
          descricao={`Preencha antes: ${faltando.join(', ')}.`}
        />
      )}

      {estado === 'erro' && <ErrorState mensagem={erro} aoTentarNovamente={() => void buscar()} />}

      {estado === 'ok' && (
        <ListaSelecao
          itens={cotas}
          chave={(c) => c.codigo_cota}
          rotulo={(c) => c.descricao_cota}
          aoEscolher={(c) => {
            onSelectCota(c);
            onClose();
          }}
          rotuloAcessivel="Cotas disponíveis para o perfil do veículo"
          vazio={
            <EmptyState
              icone="sem-dados"
              titulo="Nenhuma cota para este perfil"
              descricao="Não há cota cadastrada para esta combinação de regional, cooperativa, tipo e faixa de valor. Confira o valor protegido ou fale com o TI."
            />
          }
        />
      )}
    </Modal>
  );
};

export default ModalCotas;
